import {
  Board_BoardEnum,
  Class_ClassEnum,
  Subject_SubjectEnum,
  YearType,
} from '@protos/common/common.db_pb';

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export interface IQuestionPaperFilter {
  name: string;
  enum: Board_BoardEnum | Class_ClassEnum | Subject_SubjectEnum | YearType;
  checked: boolean;
}

// ------------------------------------------------------------
// ------------------------------------------------------------

const formatBoardName = (key: string): string => {
  if (key.startsWith('STATE_')) {
    const stateName = key
      .replace('STATE_', 'State - ')
      .replace('PRADESH', ' PRADESH')
      .replace('WEST', 'WEST ')
      .replace('JK', 'J&K');

    return stateName;
  }
  return key;
};

// export const modifiedBoardEnum: IQuestionPaperFilter[] = Object.keys(
//   Board_BoardEnum
// )
//   .filter((key) => isNaN(Number(key))) // filter reverse mapping
//   .filter((key) => key !== 'UNDEFINED') // skip UNDEFINED
//   .map((key) => ({
//     name: formatBoardName(key),
//     enum: Board_BoardEnum[key as keyof typeof Board_BoardEnum],
//     checked: false,
//   }));

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const getBoardArrayQP = (
  selectedBoards: IQuestionPaperFilter[] | undefined
) => {
  const boardArray = selectedBoards
    ?.filter(
      (item) =>
        item.checked &&
        Object.values(Board_BoardEnum).includes(item.enum as Board_BoardEnum)
    )
    .map((item) => item.enum as Board_BoardEnum);

  return boardArray;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// to create filter lists from API response data
export const createBoardListFromResponse = (
  boardList: Board_BoardEnum[]
): IQuestionPaperFilter[] => {
  return boardList.map((boardEnum) => {
    // Find the key for this enum
    const boardKey = Object.keys(Board_BoardEnum).find(
      (key) =>
        Board_BoardEnum[key as keyof typeof Board_BoardEnum] === boardEnum
    );
    return {
      name: formatBoardName(boardKey || ''),
      enum: boardEnum,
      checked: false,
    };
  });
};

// ------------------------------------------------------------
// ------------------------------------------------------------

// export const modifiedClassEnum: IQuestionPaperFilter[] = Object.keys(
//   Class_ClassEnum
// )
//   .filter((key) => isNaN(Number(key))) // Exclude reverse mappings
//   .filter((key) => key !== 'UNDEFINED') // Exclude UNDEFINED
//   .map((key) => ({
//     name: key.replace(/_/g, ' ').replace('CLASS', ''),
//     enum: Class_ClassEnum[key as keyof typeof Class_ClassEnum],
//     checked: false,
//   }));

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const getGradeArrayQP = (
  selectedBoards: IQuestionPaperFilter[] | undefined
) => {
  const boardArray = selectedBoards
    ?.filter(
      (item) =>
        item.checked &&
        Object.values(Class_ClassEnum).includes(item.enum as Class_ClassEnum)
    )
    .map((item) => item.enum as Class_ClassEnum);

  return boardArray;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// to create filter lists from API response data
export const createClassListFromResponse = (
  gradeList: Class_ClassEnum[]
): IQuestionPaperFilter[] => {
  return gradeList.map((classEnum) => {
    // Find the key for this enum
    const classKey = Object.keys(Class_ClassEnum).find(
      (key) =>
        Class_ClassEnum[key as keyof typeof Class_ClassEnum] === classEnum
    );
    return {
      name: (classKey || '').replace(/_/g, ' ').replace('CLASS', ''),
      enum: classEnum,
      checked: false,
    };
  });
};

// ------------------------------------------------------------
// ------------------------------------------------------------

const SubjectMap = {
  M: 'Mathematics',
  E: 'English',
  S: 'Science',
  SS: 'Social Science',
  SP: 'Physics',
  SC: 'Chemistry',
  SB: 'Biology',
  ELIT: 'English Literature',
  ELANG: 'English Language',
  EG: 'English Grammar',
  HIST: 'History',
  GEOG: 'Geography',
  ICT: 'Information and Communication Technology',
  EVS: 'Environmental Studies',
  CIV: 'Civics',
  ECO: 'Economics',
  GK: 'General Knowledge',
  BIZ: 'Business Studies',
  ACCT: 'Accountancy',
  STATS: 'Statistics',
  LR: 'Logical Reasoning',
  VOCAB: 'Vocabulary',
  GAME: 'Games',
  APT: 'Aptitude',
  GS: 'General Studies',
  LANG: 'Foreign Language',
  ENTMT: 'Entertainment',
  SBI_PO: 'SBI PO Exam',
  IBPS_PO: 'IBPS PO Exam',
  BANK_PO: 'Bank PO Exam',
  GOVT: 'Government Exams',
  URD: 'Urdu',
  H: 'Hindi',
  SANS: 'Sanskrit',
  MAR: 'Marathi',
  GUJ: 'Gujarati',
  SPL: 'Social and Political Life',
  PS: 'Political Science',
  TRAINING: 'Training',
  CL: 'Climate Leadership',
  ROBO: 'Robotics',
  COMP: 'Computer',
};

// export const modifiedSubjectEnum: IQuestionPaperFilter[] = Object.keys(
//   Subject_SubjectEnum
// )
//   .filter((key) => isNaN(Number(key))) // Exclude reverse mappings
//   .filter((key) => key !== 'UNDEFINED') // Exclude UNDEFINED
//   .map((key) => ({
//     name: SubjectMap[key as keyof typeof SubjectMap] ?? key,
//     enum: Subject_SubjectEnum[key as keyof typeof Subject_SubjectEnum],
//     checked: false,
//   }));

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Returns the display name for a Subject_SubjectEnum value.
 * Returns empty string for UNDEFINED.
 */
export const getSubjectNameFromEnum = (
  subjectEnum: Subject_SubjectEnum
): string => {
  if (subjectEnum === Subject_SubjectEnum.UNDEFINED) {
    return '';
  }
  const subjectKey = Object.keys(Subject_SubjectEnum).find(
    (key) =>
      Subject_SubjectEnum[key as keyof typeof Subject_SubjectEnum] ===
      subjectEnum
  );
  if (!subjectKey) return '';
  return SubjectMap[subjectKey as keyof typeof SubjectMap] ?? subjectKey;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const getSubjectArrayQP = (
  selectedBoards: IQuestionPaperFilter[] | undefined
) => {
  const boardArray = selectedBoards
    ?.filter(
      (item) =>
        item.checked &&
        Object.values(Subject_SubjectEnum).includes(
          item.enum as Subject_SubjectEnum
        )
    )
    .map((item) => item.enum as Subject_SubjectEnum);

  return boardArray;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// to create filter lists from API response data
export const createSubjectListFromResponse = (
  subjectList: Subject_SubjectEnum[]
): IQuestionPaperFilter[] => {
  return subjectList.map((subjectEnum) => {
    // Find the key for this enum
    const subjectKey = Object.keys(Subject_SubjectEnum).find(
      (key) =>
        Subject_SubjectEnum[key as keyof typeof Subject_SubjectEnum] ===
        subjectEnum
    );
    return {
      name: SubjectMap[subjectKey as keyof typeof SubjectMap] ?? subjectKey,
      enum: subjectEnum,
      checked: false,
    };
  });
};

// ------------------------------------------------------------
// ------------------------------------------------------------

// export const modifiedYearEnum: IQuestionPaperFilter[] = Object.keys(YearType)
//   .filter((key) => isNaN(Number(key))) // Exclude reverse mappings
//   .filter((key) => key !== 'YEAR_UNDEFINED') // Exclude UNDEFINED
//   .map((key) => ({
//     name: key.replace('YEAR_', ''),
//     enum: YearType[key as keyof typeof YearType],
//     checked: false,
//   }));

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const getYearsArrayQP = (
  selectedBoards: IQuestionPaperFilter[] | undefined
) => {
  const boardArray = selectedBoards
    ?.filter(
      (item) =>
        item.checked && Object.values(YearType).includes(item.enum as YearType)
    )
    .map((item) => item.enum as YearType);

  return boardArray;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// to create filter lists from API response data
export const createYearListFromResponse = (
  yearList: YearType[]
): IQuestionPaperFilter[] => {
  return yearList
    .map((yearEnum) => {
      // Find the key for this enum
      const yearKey = Object.keys(YearType).find(
        (key) => YearType[key as keyof typeof YearType] === yearEnum
      );
      return {
        name: (yearKey || '').replace('YEAR_', ''),
        enum: yearEnum,
        checked: false,
      };
    })
    .sort((a, b) => Number(b.name) - Number(a.name));
};

// ------------------------------------------------------------
// ------------------------------------------------------------
